import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Inject,
	OnDestroy,
	OnInit,
} from "@angular/core";
import { AbstractControl, FormBuilder, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Subject, finalize, takeUntil } from "rxjs";

import { ContactService } from "../../services/contact.service";
import {
	ContactDetailDto,
	PuestoDto,
} from "../../interfaces/contact.interface";
import { AuthService } from "src/app/_core/providers/auth.service";
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";
import { ImageModalService } from "src/app/_core/services/image-modal.service";
import { DialogService } from "src/app/_core/services/dialog.service";

export type ContactDialogMode = "view" | "edit" | "create";

export interface ContactDialogData {
	mode: ContactDialogMode;
	/** Requerido en view/edit. */
	contactId?: number;
	/** Requeridos en create (entityType admite inglés `centros/...` o español `Centro/...`). */
	entityId?: number;
	entityType?: string;
	entityName?: string;
}

export interface ContactDialogResult {
	changed: boolean;
	action?: "created" | "updated" | "deleted";
	contact?: ContactDetailDto;
}

/** Mapeo a la forma española que espera el backend (ContactDto.entidad). */
const ENTITY_TYPE_TO_SPANISH: Record<string, string> = {
	contacts: "Centro",
	opportunities: "Oportunidad",
	sites: "Centro",
	cebes: "Cebe",
	contracts: "Contrato",
	centros: "Centro",
};

const DEFAULT_PHOTO = "/assets/images/user.png";

/**
 * Diálogo **integral** de Persona (contacto): **ver / editar / crear** en un único
 * diálogo grande. Sustituye por completo las pantallas ruteadas `/people/detail` y
 * `/people/form` (decisión Pablo 2026-06-05).
 *
 * - `view`: muestra el detalle (cabecera + tarjetas) tal como la pantalla anterior.
 * - `edit`: edición in-place (puesto + observaciones); al guardar vuelve a `view`.
 * - `create`: alta asociada a una entidad (empleado → puesto por función + observaciones).
 *
 * Emite `changed` en cada mutación (para refresco en vivo del caller) y además
 * devuelve `ContactDialogResult` al cerrar (`afterClosed`).
 */
@Component({
	selector: "app-contact-dialog",
	templateUrl: "./contact-dialog.component.html",
	styleUrls: ["./contact-dialog.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class ContactDialogComponent implements OnInit, OnDestroy {
	private readonly destroy$ = new Subject<void>();

	/** Eventos de mutación hacia el caller (refresco en vivo sin cerrar). */
	readonly changed = new Subject<ContactDialogResult>();

	mode: ContactDialogMode;
	contact: ContactDetailDto | null = null;
	photo: string | ArrayBuffer = DEFAULT_PHOTO;
	hasError = false;

	/** Acumula si algo cambió, para devolverlo en el resultado de cierre. */
	private dirtyResult: ContactDialogResult = { changed: false };
	private empleadoFuncion: string | null = null;

	readonly form = this.fb.group({
		empleadoId: [null as number | null, [Validators.required]],
		puestoId: [{ value: null as number | null, disabled: true }, [Validators.required]],
		observaciones: [""],
	});

	private readonly loadingSubject = new BehaviorSubject<boolean>(false);
	private readonly savingSubject = new BehaviorSubject<boolean>(false);
	private readonly puestosSubject = new BehaviorSubject<readonly PuestoDto[]>([]);
	private readonly loadingPuestosSubject = new BehaviorSubject<boolean>(false);
	private readonly empleadoSinFuncionSubject = new BehaviorSubject<boolean>(false);

	readonly isLoading$ = this.loadingSubject.asObservable();
	readonly isSaving$ = this.savingSubject.asObservable();
	readonly puestos$ = this.puestosSubject.asObservable();
	readonly isLoadingPuestos$ = this.loadingPuestosSubject.asObservable();
	readonly empleadoSinFuncion$ = this.empleadoSinFuncionSubject.asObservable();

	constructor(
		private readonly fb: FormBuilder,
		private readonly contactService: ContactService,
		private readonly notificationService: NotificationService,
		private readonly translate: TranslateService,
		private readonly authService: AuthService,
		private readonly masterService: MasterService,
		private readonly imageModalService: ImageModalService,
		private readonly dialogService: DialogService,
		private readonly cdr: ChangeDetectorRef,
		public readonly dialogRef: MatDialogRef<ContactDialogComponent, ContactDialogResult>,
		@Inject(MAT_DIALOG_DATA) public readonly data: ContactDialogData,
	) {
		this.mode = data.mode;
	}

	ngOnInit(): void {
		if (this.mode === "create") {
			return;
		}
		if (this.data.contactId) {
			this.loadContact(this.data.contactId);
		} else {
			this.hasError = true;
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.changed.complete();
	}

	get isView(): boolean {
		return this.mode === "view";
	}
	get isEdit(): boolean {
		return this.mode === "edit";
	}
	get isCreate(): boolean {
		return this.mode === "create";
	}

	get titleKey(): string {
		if (this.isCreate) return "title.createContact";
		if (this.isEdit) return "contacts.form.edit_title";
		return "title.contactDetail";
	}
	get headIcon(): string {
		if (this.isCreate) return "person_add";
		if (this.isEdit) return "edit";
		return "badge";
	}

	get entityTypeKey(): string {
		const map: Record<string, string> = {
			Oportunidad: "opportunities",
			Contrato: "contracts",
			Cebe: "cebes",
			Centro: "centros",
		};
		const t = this.contact?.entidad || this.data.entityType || "";
		const k = map[t] || t.toLowerCase();
		return `common.entityTypes.${k}`;
	}

	get hasPhoto(): boolean {
		return !!this.photo && this.photo !== DEFAULT_PHOTO;
	}

	// ---- Carga ----

	private loadContact(id: number): void {
		this.loadingSubject.next(true);
		this.hasError = false;
		this.cdr.markForCheck();

		this.contactService
			.getContactById(id)
			.pipe(
				takeUntil(this.destroy$),
				finalize(() => {
					this.loadingSubject.next(false);
					this.cdr.markForCheck();
				}),
			)
			.subscribe({
				next: (contact) => {
					this.contact = contact;
					this.resolveEmpleadoMeta(contact.empleadoId);
					if (this.mode === "edit") {
						this.populateFormFromContact();
					}
				},
				error: () => {
					this.hasError = true;
					this.notificationService.showError(
						this.translate.instant("contacts.detail.loadError"),
					);
				},
			});
	}

	/** Resuelve email (foto) y función agregada (puestos en edición) del empleado. */
	private resolveEmpleadoMeta(empleadoId?: number): void {
		if (!empleadoId) return;
		this.masterService
			.getEmpleados()
			.pipe(takeUntil(this.destroy$))
			.subscribe((empleados) => {
				const emp = empleados.find((e) => e.id === empleadoId);
				if (emp?.email) {
					this.loadPhoto(emp.email);
				}
				this.empleadoFuncion = emp?.funcionAgregada ?? null;
				if (this.mode === "edit") {
					// Sin función agregada también se carga: el back devuelve los roles generales (ADR-0005).
					this.loadPuestos(this.empleadoFuncion ?? "", this.form.get("puestoId"), this.contact?.puestoId ?? null);
				}
			});
	}

	private loadPhoto(email: string): void {
		this.authService.getUserPhoto(email).subscribe(
			(photoResult) => {
				if (!photoResult) return;
				const reader = new FileReader();
				reader.addEventListener("load", () => {
					this.photo = reader.result || DEFAULT_PHOTO;
					this.cdr.markForCheck();
				});
				reader.readAsDataURL(photoResult);
			},
			() => {
				this.photo = DEFAULT_PHOTO;
			},
		);
	}

	showEmployeePhoto(): void {
		if (!this.hasPhoto) return;
		this.imageModalService
			.showImage(this.photo as string, this.contact?.empleadoNombre || "")
			.subscribe();
	}

	// ---- Empleado / puestos (create + edit) ----

	onEmpleadoSelected(empleado: any): void {
		const puestoControl = this.form.get("puestoId");
		if (!empleado) {
			this.resetPuesto(puestoControl);
			this.empleadoSinFuncionSubject.next(false);
			return;
		}
		if (!empleado.funcionAgregada) {
			// Sin función agregada el alta YA NO se bloquea: se ofrecen los roles
			// generales (ADR-0005) y el hint queda como aviso informativo.
			this.empleadoSinFuncionSubject.next(true);
			this.loadPuestos("", puestoControl);
			return;
		}
		this.empleadoSinFuncionSubject.next(false);
		this.loadPuestos(empleado.funcionAgregada, puestoControl);
	}

	private resetPuesto(ctrl: AbstractControl | null): void {
		this.puestosSubject.next([]);
		ctrl?.disable();
		ctrl?.setValue(null);
	}

	private loadPuestos(
		funcion: string,
		ctrl: AbstractControl | null,
		preselectId: number | null = null,
	): void {
		this.loadingPuestosSubject.next(true);
		ctrl?.disable();
		this.contactService
			.getPuestosByFuncion(funcion)
			.pipe(
				takeUntil(this.destroy$),
				finalize(() => {
					this.loadingPuestosSubject.next(false);
					this.cdr.markForCheck();
				}),
			)
			.subscribe({
				next: (puestos) => {
					this.puestosSubject.next(puestos);
					ctrl?.enable();
					if (preselectId != null) {
						ctrl?.setValue(preselectId);
					}
				},
				error: () => this.puestosSubject.next([]),
			});
	}

	getPuestoDisplayText(p: PuestoDto): string {
		return `${p.codigo || "N/A"} - ${p.description}`;
	}

	/** Etiqueta de opción para app-ds-select (referencia estable, conserva `this`). */
	puestoLabelOf = (p: PuestoDto): string => this.getPuestoDisplayText(p);

	// ---- Transiciones de modo ----

	enterEdit(): void {
		if (!this.contact) return;
		this.mode = "edit";
		this.populateFormFromContact();
		if (this.empleadoFuncion) {
			this.loadPuestos(this.empleadoFuncion, this.form.get("puestoId"), this.contact.puestoId ?? null);
		}
		this.cdr.markForCheck();
	}

	cancelEdit(): void {
		// Si entramos al diálogo en modo create o edit puro (sin vista previa), cancelar cierra el
		// diálogo. Solo cuando entramos en "view" (y pasamos a editar in-place) cancelar vuelve a view.
		if (this.data.mode === "create" || this.data.mode === "edit") {
			this.close();
			return;
		}
		this.mode = "view";
		this.cdr.markForCheck();
	}

	private populateFormFromContact(): void {
		if (!this.contact) return;
		this.form.patchValue({
			empleadoId: this.contact.empleadoId,
			puestoId: this.contact.puestoId,
			observaciones: this.contact.observaciones || "",
		});
		// Puesto se habilita cuando cargan los puestos de la función.
		this.form.get("puestoId")?.disable();
	}

	// ---- Guardar ----

	save(): void {
		if (this.isCreate) {
			this.create();
		} else if (this.isEdit) {
			this.update();
		}
	}

	private create(): void {
		if (this.form.invalid || !this.data.entityId) {
			this.form.markAllAsTouched();
			this.notificationService.showError(
				"Por favor, completa todos los campos obligatorios",
			);
			return;
		}
		this.savingSubject.next(true);
		this.cdr.markForCheck();
		const v = this.form.getRawValue();
		this.contactService
			.createContact({
				empleadoId: v.empleadoId || 0,
				puestoId: v.puestoId || 0,
				entidadId: this.data.entityId || 0,
				entidad: this.entidadSpanish,
				observaciones: (v.observaciones || "").trim(),
			})
			.pipe(
				takeUntil(this.destroy$),
				finalize(() => {
					this.savingSubject.next(false);
					this.cdr.markForCheck();
				}),
			)
			.subscribe({
				next: (created) => {
					this.notificationService.showSuccess(
						"Contacto creado correctamente",
						this.translate.instant("common.success"),
					);
					this.emitChange("created", created);
					this.close();
				},
				error: () =>
					this.notificationService.showError("Error al crear el contacto"),
			});
	}

	private update(): void {
		if (!this.contact || this.form.invalid) {
			this.form.markAllAsTouched();
			this.notificationService.showError(
				"Por favor, completa todos los campos obligatorios",
			);
			return;
		}
		this.savingSubject.next(true);
		this.cdr.markForCheck();
		const v = this.form.getRawValue();
		const id = this.contact.id;
		this.contactService
			.updateContact(id, {
				empleadoId: this.contact.empleadoId,
				puestoId: v.puestoId || this.contact.puestoId,
				entidadId: this.contact.entidadId,
				entidad: this.contact.entidad || "",
				observaciones: (v.observaciones || "").trim(),
			})
			.pipe(
				takeUntil(this.destroy$),
				finalize(() => {
					this.savingSubject.next(false);
					this.cdr.markForCheck();
				}),
			)
			.subscribe({
				next: (updated) => {
					this.notificationService.showSuccess(
						this.translate.instant("contacts.editSuccess"),
						this.translate.instant("common.success"),
					);
					this.contact = updated || { ...this.contact, ...v } as ContactDetailDto;
					this.emitChange("updated", this.contact);
					// Vuelve a la vista de detalle con los datos frescos.
					this.mode = "view";
					this.cdr.markForCheck();
				},
				error: () =>
					this.notificationService.showError(
						this.translate.instant("contacts.editError"),
					),
			});
	}

	// ---- Eliminar ----

	delete(): void {
		if (!this.contact) return;
		this.dialogService
			.confirm({
				type: "danger",
				title: this.translate.instant("contacts.deleteContact"),
				subtitle: this.contact.empleadoNombre ?? "",
				message: this.translate.instant("contacts.confirmDelete", {
					name: this.contact.empleadoNombre ?? "",
				}),
				confirmText: this.translate.instant("common.delete"),
				cancelText: this.translate.instant("common.cancel"),
				confirmTone: "danger",
			})
			.pipe(takeUntil(this.destroy$))
			.subscribe((confirmed) => {
				if (confirmed) {
					this.performDelete();
				}
			});
	}

	private performDelete(): void {
		if (!this.contact) return;
		this.savingSubject.next(true);
		this.cdr.markForCheck();
		const id = this.contact.id;
		this.contactService
			.deleteContact(id)
			.pipe(
				takeUntil(this.destroy$),
				finalize(() => {
					this.savingSubject.next(false);
					this.cdr.markForCheck();
				}),
			)
			.subscribe({
				next: () => {
					this.notificationService.showSuccess(
						this.translate.instant("contacts.deleteSuccess"),
						this.translate.instant("common.success"),
					);
					this.emitChange("deleted");
					this.close();
				},
				error: () =>
					this.notificationService.showError(
						this.translate.instant("contacts.deleteError"),
					),
			});
	}

	// ---- Cierre / eventos ----

	private emitChange(action: ContactDialogResult["action"], contact?: ContactDetailDto): void {
		this.dirtyResult = { changed: true, action, contact };
		this.changed.next(this.dirtyResult);
	}

	close(): void {
		this.dialogRef.close(this.dirtyResult);
	}

	private get entidadSpanish(): string {
		const t = this.data.entityType || "";
		return ENTITY_TYPE_TO_SPANISH[t.toLowerCase()] || t;
	}
}
